import { motion } from 'motion/react'
import { useInView } from 'motion/react'
import { useRef, useState } from 'react'
import { products, fontSans, hexToRgba, SENTINEL_DEMO_URL } from './theme'

const productDetails = {
  platform: {
    icon: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <ellipse cx="12" cy="5" rx="8" ry="3" />
        <path d="M4 5v6c0 1.66 3.58 3 8 3s8-1.34 8-3V5" />
        <path d="M4 11v6c0 1.66 3.58 3 8 3s8-1.34 8-3v-6" />
      </svg>
    ),
    features: [
      'Ingest from PostgreSQL, MySQL, MSSQL, CSV & more',
      'Automatic cataloging, profiling & semantic search',
      'PII detection with configurable masking strategies',
      'Quality checks, lineage & crash-safe pipeline runs',
    ],
    cta: 'Explore the Platform',
    href: '#platform',
  },
  ontext: {
    icon: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="5" cy="6" r="2.5" />
        <circle cx="19" cy="6" r="2.5" />
        <circle cx="12" cy="13" r="3" />
        <circle cx="5" cy="20" r="2.5" />
        <circle cx="19" cy="20" r="2.5" />
        <path d="M7.2 6.8 9.6 11M16.8 6.8 14.4 11M7.2 19.2 9.6 15M16.8 19.2 14.4 15" />
      </svg>
    ),
    features: [
      'Define entities in plain English — confirm before commit',
      'Serve any AI agent over MCP & REST',
      'Field-level masking & trust tiers applied at query time',
      'Versioned ontology with a full audit trail',
    ],
    cta: 'Discover Ontext',
    href: '#ontext',
  },
  sentinel: {
    icon: (
      <svg
        width="26"
        height="26"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <path d="M12 2 4 5.5v5c0 5 3.4 9.4 8 11.5 4.6-2.1 8-6.5 8-11.5v-5L12 2Z" />
        <path d="M12 8v4.5" />
        <circle cx="12" cy="15.5" r="0.5" fill="currentColor" />
      </svg>
    ),
    features: [
      'Scan datasets, prompts & free text for 25+ PII types',
      'Mask with hash, redact or partial-mask strategies',
      'Risk scoring & audit evidence for GDPR / DPDP / EU AI Act',
      'Runs 100% in your browser — free, standalone, forever',
    ],
    cta: 'Try the Live Demo',
    href: SENTINEL_DEMO_URL,
  },
} as const

function ProductCard({
  product,
  delay,
}: {
  product: (typeof products)[number]
  delay: number
}) {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-60px' })
  const [hovered, setHovered] = useState(false)
  const details = productDetails[product.id]
  const isExternal = details.href.startsWith('http')

  return (
    <motion.a
      ref={ref}
      href={details.href}
      {...(isExternal
        ? { target: '_blank', rel: 'noopener noreferrer' }
        : {})}
      initial={{ opacity: 0, y: 40 }}
      animate={inView ? { opacity: 1, y: 0 } : {}}
      transition={{ duration: 0.65, delay, ease: [0.22, 1, 0.36, 1] }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        display: 'flex',
        flexDirection: 'column',
        padding: '2.25rem',
        borderRadius: '20px',
        background: hovered
          ? hexToRgba(product.accent, 0.07)
          : 'rgba(255,255,255,0.025)',
        border: `1px solid ${
          hovered ? hexToRgba(product.accent, 0.35) : 'rgba(255,255,255,0.07)'
        }`,
        transition: 'all 0.3s ease',
        transform: hovered ? 'translateY(-6px)' : 'translateY(0)',
        boxShadow: hovered ? `0 24px 60px ${hexToRgba(product.accent, 0.12)}` : 'none',
        textDecoration: 'none',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Top accent line */}
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: '2rem',
          right: '2rem',
          height: '1px',
          background: `linear-gradient(90deg, transparent, ${hexToRgba(product.accent, 0.6)}, transparent)`,
          opacity: hovered ? 1 : 0.4,
          transition: 'opacity 0.3s ease',
        }}
      />

      {/* Icon + badge */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          marginBottom: '1.5rem',
          gap: '0.75rem',
        }}
      >
        <div
          style={{
            width: '56px',
            height: '56px',
            borderRadius: '14px',
            background: hexToRgba(product.accent, 0.12),
            border: `1px solid ${hexToRgba(product.accent, 0.3)}`,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: product.accent,
            transition: 'all 0.3s ease',
            ...(hovered
              ? {
                  boxShadow: `0 0 24px ${hexToRgba(product.accent, 0.25)}`,
                  borderColor: hexToRgba(product.accent, 0.55),
                }
              : {}),
            flexShrink: 0,
          }}
        >
          {details.icon}
        </div>
        <span
          style={{
            fontFamily: fontSans,
            fontSize: '0.68rem',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            color: product.accent,
            background: hexToRgba(product.accent, 0.09),
            border: `1px solid ${hexToRgba(product.accent, 0.28)}`,
            borderRadius: '100px',
            padding: '5px 12px',
            whiteSpace: 'nowrap',
          }}
        >
          {product.badge}
        </span>
      </div>

      <h3
        style={{
          fontFamily: fontSans,
          fontSize: '1.45rem',
          fontWeight: 700,
          color: '#ffffff',
          letterSpacing: '-0.02em',
          margin: '0 0 2px',
        }}
      >
        {product.name}
      </h3>
      <div
        style={{
          fontFamily: fontSans,
          fontSize: '0.8rem',
          fontWeight: 600,
          color: product.accent,
          letterSpacing: '0.02em',
          marginBottom: '0.9rem',
        }}
      >
        {product.tagline}
      </div>
      <p
        style={{
          fontFamily: fontSans,
          fontSize: '0.9rem',
          color: 'rgba(255,255,255,0.55)',
          lineHeight: 1.7,
          margin: '0 0 1.5rem',
        }}
      >
        {product.description}
      </p>

      {/* Features */}
      <ul
        style={{
          listStyle: 'none',
          padding: 0,
          margin: '0 0 1.75rem',
          display: 'flex',
          flexDirection: 'column',
          gap: '0.6rem',
          flexGrow: 1,
        }}
      >
        {details.features.map((f) => (
          <li
            key={f}
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: '10px',
              fontFamily: fontSans,
              fontSize: '0.84rem',
              color: 'rgba(255,255,255,0.68)',
              lineHeight: 1.5,
            }}
          >
            <svg
              width="15"
              height="15"
              viewBox="0 0 16 16"
              fill="none"
              style={{
                flexShrink: 0,
                marginTop: '2px',
                color: product.accent,
              }}
            >
              <circle
                cx="8"
                cy="8"
                r="7"
                stroke="currentColor"
                strokeWidth="1.4"
                opacity="0.4"
              />
              <path
                d="M5 8.2 7.1 10.3 11 6"
                stroke="currentColor"
                strokeWidth="1.6"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
            {f}
          </li>
        ))}
      </ul>

      {/* CTA row */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '8px',
          fontFamily: fontSans,
          fontSize: '0.88rem',
          fontWeight: 600,
          color: product.accent,
        }}
      >
        {details.cta}
        <svg
          width="15"
          height="15"
          viewBox="0 0 16 16"
          fill="none"
          style={{
            transition: 'transform 0.25s ease',
            transform: hovered ? 'translateX(4px)' : 'translateX(0)',
          }}
        >
          <path
            d="M2 8h11M9 3.5 13.5 8 9 12.5"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </div>
    </motion.a>
  )
}

export function Family() {
  const ref = useRef(null)
  const inView = useInView(ref, { once: true, margin: '-100px' })

  return (
    <section
      id="products"
      style={{
        padding: '7rem 2rem',
        background: 'linear-gradient(180deg, #0A0F1F 0%, #0c1222 100%)',
        position: 'relative',
      }}
    >
      <div
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '1px',
          background:
            'linear-gradient(90deg, transparent, rgba(74, 144, 226, 0.3), rgba(45, 212, 191, 0.3), transparent)',
        }}
      />

      <div style={{ maxWidth: '1280px', margin: '0 auto' }}>
        <div style={{ textAlign: 'center', marginBottom: '4rem' }}>
          <motion.div
            ref={ref}
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.5 }}
          >
            <span
              style={{
                fontFamily: fontSans,
                fontSize: '0.75rem',
                fontWeight: 600,
                letterSpacing: '0.12em',
                textTransform: 'uppercase',
                color: '#4A90E2',
                display: 'block',
                marginBottom: '1rem',
              }}
            >
              The SynKrasis Family
            </span>
            <h2
              style={{
                fontFamily: fontSans,
                fontSize: 'clamp(2rem, 4vw, 3rem)',
                fontWeight: 800,
                color: '#ffffff',
                letterSpacing: '-0.03em',
                lineHeight: 1.1,
                marginBottom: '1.25rem',
              }}
            >
              One Governance Kernel.{' '}
              <span
                style={{
                  background:
                    'linear-gradient(135deg, #4A90E2, #9D4EDD, #2DD4BF)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                Three Products.
              </span>
            </h2>
            <p
              style={{
                fontFamily: fontSans,
                fontSize: '1.05rem',
                color: 'rgba(255,255,255,0.5)',
                maxWidth: '640px',
                margin: '0 auto',
                lineHeight: 1.7,
              }}
            >
              Every product stands perfectly on its own — start with the pain
              you have today. Together, they compound into a complete agentic
              data platform.
            </p>
          </motion.div>
        </div>

        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '1.5rem',
            alignItems: 'stretch',
          }}
        >
          {products.map((p, i) => (
            <ProductCard key={p.id} product={p} delay={i * 0.12} />
          ))}
        </div>
      </div>
    </section>
  )
}
